import { driveSourcesKeys, memberExternalAccountKeys, xeroConnectionKeys } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function useIntegrationCallbackParams(memberId: number): void {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastHandledCallbackKeyRef = useRef<string | null>(null);

  const callbackProvider = searchParams.get('provider');
  const callbackStatus = searchParams.get('status');
  const callbackReason = searchParams.get('reason');
  const callbackAccountLabel =
    callbackProvider === 'google' ? 'Google' : callbackProvider === 'microsoft' ? 'Microsoft' : null;
  const hasAccountCallbackParams =
    callbackAccountLabel !== null && (callbackStatus === 'success' || callbackStatus === 'error');
  const callbackIntegration = searchParams.get('integration');
  const hasXeroCallbackParams =
    callbackIntegration === 'xero' && (callbackStatus === 'success' || callbackStatus === 'error');
  const hasMicrosoftTenantCallbackParams =
    callbackIntegration === 'microsoft-tenant' && (callbackStatus === 'success' || callbackStatus === 'error');
  const hasGoogleDriveCallbackParams =
    callbackIntegration === 'google-drive' && (callbackStatus === 'success' || callbackStatus === 'error');

  useEffect(() => {
    if (!hasAccountCallbackParams) {
      return;
    }

    const callbackKey = `${callbackProvider}:${callbackStatus}:${callbackReason ?? ''}:${searchParams.toString()}`;
    if (lastHandledCallbackKeyRef.current === callbackKey) {
      return;
    }
    lastHandledCallbackKeyRef.current = callbackKey;

    if (callbackStatus === 'success') {
      toast.success(`${callbackAccountLabel} account connected.`);
      if (memberId > 0) {
        queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.list(memberId) });
      } else {
        queryClient.invalidateQueries({ queryKey: memberExternalAccountKeys.lists() });
      }
    } else {
      const safeReason = callbackReason ? callbackReason.replace(/_/g, ' ') : 'unknown error';
      toast.error(`${callbackAccountLabel} connection failed: ${safeReason}.`);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('provider');
    nextParams.delete('status');
    nextParams.delete('reason');
    setSearchParams(nextParams, { replace: true });
  }, [
    callbackProvider,
    callbackReason,
    callbackAccountLabel,
    callbackStatus,
    hasAccountCallbackParams,
    memberId,
    queryClient,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!hasXeroCallbackParams && !hasMicrosoftTenantCallbackParams && !hasGoogleDriveCallbackParams) {
      return;
    }

    const callbackKey = `integration:${callbackIntegration}:${callbackStatus}:${callbackReason ?? ''}:${searchParams.toString()}`;
    if (lastHandledCallbackKeyRef.current === callbackKey) {
      return;
    }
    lastHandledCallbackKeyRef.current = callbackKey;

    const label = hasXeroCallbackParams ? 'Xero' : hasGoogleDriveCallbackParams ? 'Google Drive' : 'Microsoft tenant';
    if (callbackStatus === 'success') {
      toast.success(`${label} authorisation completed.`);
    } else {
      const safeReason = callbackReason ? callbackReason.replace(/_/g, ' ') : 'unknown error';
      toast.error(`${label} connection failed: ${safeReason}.`);
    }
    queryClient.invalidateQueries({
      queryKey: hasXeroCallbackParams ? xeroConnectionKeys.all : driveSourcesKeys.all,
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('integration');
    nextParams.delete('status');
    nextParams.delete('reason');
    setSearchParams(nextParams, { replace: true });
  }, [
    callbackIntegration,
    callbackReason,
    callbackStatus,
    hasGoogleDriveCallbackParams,
    hasMicrosoftTenantCallbackParams,
    hasXeroCallbackParams,
    queryClient,
    searchParams,
    setSearchParams,
  ]);
}
